import React, { useState, useEffect, useMemo } from "react";
import { SUPABASE_READY } from "./supabase";
import { WEEKLY_DEALS, WEEKLY_SCAN_WEEK } from "./seedData";
import { T, SANS, SERIF, useNow, buzzLight } from "./components";
import { WeeklyTab } from "./weekly";
import { ProfileTab } from "./profile";
import { StoreMap } from "./map";
import {
  loadLocPref, saveLocPref, requestPosition, GEO_SUPPORTED,
} from "./location";
import {
  useSession, sendMagicLink, signOut as authSignOut,
  ensureProfile, getProfile, setDisplayName, AUTH_READY,
} from "./auth";
import {
  useGroceryList, countListMatches, GroceryListTab,
} from "./groceryList";
import { IconInfo, IconTag, IconUser, IconCart, IconPin } from "./icons";
import { pokeFun, bumpSave, savingsScore } from "./play";

// ─── v3.0: pure weekly flyer-deals app ────────────────────────────────────
// The closing-time vision (closing feed, map, countdowns, "Still there?",
// meal picker, ImpactBar, Kitchen Rescue) is unwired but kept on disk in
// components.js / map.js / meals.js / seedData.js — it can return later.
// Tabs: Deals (landing) | List | Profile.

// ─── App ─────────────────────────────────────────────────────────────────
export default function App() {
  const now = useNow(30000);
  const [tab, setTab] = useState("deals"); // "deals" | "nearby" | "list" | "profile" — deals is the landing tab
  // v2.1: tab tap counters drive the springy icon pop (pageflows-style
  // micro-interaction); every switch also fires a light haptic.
  const [tabTaps, setTabTaps] = useState({ deals: 0, nearby: 0, list: 0, profile: 0 });
  function selectTab(t) {
    buzzLight();
    setTab(t);
    setTabTaps((prev) => ({ ...prev, [t]: (prev[t] || 0) + 1 }));
  }

  // v3.0: store filter lifted here so the Nearby map can deep-link into it.
  const [dealStore, setDealStore] = useState("all");

  // v3.11: header logo = home button. Bumping homeNonce re-keys the tab
  // content so WeeklyTab remounts fresh (search/category cleared) — a true
  // "back to the homepage", not just a tab switch.
  const [homeNonce, setHomeNonce] = useState(0);
  function goHome() {
    buzzLight();
    setDealStore("all");
    selectTab("deals");
    setHomeNonce((n) => n + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  // v3.0: playful gamification state (localStorage, works in demo mode).
  const [fun, setFun] = useState(() => pokeFun());

  // ── v2.0: grocery list (watchlist) ──
  const { items: groceryItems, add: addGrocery, remove: removeGrocery } = useGroceryList();

  // v2.4.1: weekly bookmark is a toggle — tap again to remove from the list.
  // v3.0: adds also feed the lifetime save counter (badges).
  const toggleSaveDeal = (deal) => {
    const key = (deal.item || "").trim().toLowerCase();
    const existing = groceryItems.find((i) => (i.text || "").toLowerCase() === key);
    if (existing) removeGrocery(existing.id);
    else {
      addGrocery(deal.item);
      const n = bumpSave();
      setFun((f) => ({ ...f, saves: n }));
    }
  };

  // v3.0: "You've saved $X this week" — from saved items with a was-price.
  const savings = useMemo(
    () => savingsScore(groceryItems, WEEKLY_DEALS),
    [groceryItems]
  );
  const funStats = {
    saves: fun.saves,
    streak: fun.streak,
    nightOwl: fun.nightOwl,
    savedTotal: savings.total,
    savedCounted: savings.counted,
    listCount: groceryItems.length,
  };

  // ── v1.3: location (nearby-first; enable from the Profile tab) ──
  const [locPref, setLocPref] = useState(loadLocPref); // {status, lat, lng}
  const [locBusy, setLocBusy] = useState(false);
  const loc = locPref.status === "granted" && locPref.lat != null
    ? { lat: locPref.lat, lng: locPref.lng }
    : null;

  async function handleAllowLocation() {
    if (!GEO_SUPPORTED) {
      const pref = { status: "denied", ts: Date.now() };
      saveLocPref(pref);
      setLocPref(pref);
      return;
    }
    setLocBusy(true);
    try {
      const pos = await requestPosition();
      const pref = { status: "granted", lat: pos.lat, lng: pos.lng, ts: Date.now() };
      saveLocPref(pref);
      setLocPref(pref);
    } catch {
      const pref = { status: "denied", ts: Date.now() };
      saveLocPref(pref);
      setLocPref(pref);
    } finally {
      setLocBusy(false);
    }
  }

  // ── v1.3: accounts + badges ──
  const { user, authLoaded } = useSession();
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  // v3.0: no posting anymore, so rescued-lbs stays 0; the Profile tab
  // keeps rendering its stat row unchanged.
  const [lbsRescued] = useState(0);

  // Load (or lazily create) the signed-in user's profile row.
  useEffect(() => {
    if (!AUTH_READY || !authLoaded) return;
    if (!user) {
      setProfile(null);
      setProfileLoading(false);
      return;
    }
    setProfileLoading(true);
    ensureProfile(user).then((p) => {
      setProfile(p);
      setProfileLoading(false);
    });
  }, [user, authLoaded]);

  async function handleSignOut() {
    await authSignOut();
    setProfile(null);
  }

  async function handleSaveName(name) {
    if (!user) return;
    const clean = await setDisplayName(user.id, name);
    const fresh = await getProfile(user.id);
    if (fresh) setProfile(fresh);
    else setProfile((p) => (p ? { ...p, display_name: clean } : p));
  }

  // v2.0: grocery-list tab badge — items currently on sale in the flyers.
  const listMatchCount = useMemo(
    () => countListMatches(groceryItems, WEEKLY_DEALS, now),
    [groceryItems, now]
  );

  return (
    <div style={styles.app}>
      <style>{GLOBAL_CSS}</style>

      <header style={styles.header}>
        <div style={styles.headerInner}>
          <div style={styles.eyebrow}>Calgary · {WEEKLY_SCAN_WEEK}</div>
          <button
            type="button"
            aria-label="Grocery Deals — back to home"
            onClick={goHome}
            title="Back to home"
            style={{
              ...styles.brand, cursor: "pointer",
              background: "transparent", border: "none", padding: 0, textAlign: "left",
            }}
          >
            <img
              src={`${process.env.PUBLIC_URL}/logo-gd-tag.png`}
              alt="Grocery Deals logo"
              draggable={false}
              style={{ height: 56, width: "auto", flexShrink: 0, display: "block", filter: "drop-shadow(0 4px 8px rgba(180,30,20,.25))" }}
            />
            Grocery Deals
          </button>
        </div>
      </header>

      <main style={styles.inner}>
        {!SUPABASE_READY && (
          <div style={styles.demoBanner}>
            <IconInfo size={15} />
            <span>
              Demo mode — add your Supabase keys in the <b>Profile tab → Connection</b> for
              live sync.
            </span>
          </div>
        )}

        {/* v2.1: tab content cross-fades on switch — no instant jumps */}
        <div key={`${tab}-${homeNonce}`} style={{ animation: "fadein 0.28s ease" }}>
        {tab === "deals" ? (
          <WeeklyTab
            loc={loc}
            savedItems={groceryItems}
            onSaveItem={toggleSaveDeal}
            store={dealStore}
            onStoreChange={setDealStore}
            streak={fun.streak}
          />
        ) : tab === "nearby" ? (
          <StoreMap
            loc={loc}
            locStatus={locPref.status}
            onLocate={handleAllowLocation}
            locBusy={locBusy}
            savedItems={groceryItems}
            onSaveItem={toggleSaveDeal}
            onViewStore={(id) => { setDealStore(id); selectTab("deals"); }}
          />
        ) : tab === "list" ? (
          <GroceryListTab
            items={groceryItems}
            onAdd={addGrocery}
            onRemove={removeGrocery}
            weeklyDeals={WEEKLY_DEALS}
            nowMs={now}
          />
        ) : (
          <ProfileTab
            user={user}
            authLoaded={authLoaded}
            profile={profile}
            profileLoading={profileLoading}
            onSendLink={sendMagicLink}
            onSignOut={handleSignOut}
            onSaveName={handleSaveName}
            lbsRescued={lbsRescued}
            locPref={locPref}
            locBusy={locBusy}
            onLocate={handleAllowLocation}
            funStats={funStats}
          />
        )}
        </div>

        <div style={styles.footer}>
          Flyer prices verified from this week's flyers and r/Calgary's weekly
          thread — prices change, confirm in-store before you shop.
        </div>
      </main>

      <nav style={styles.tabbar} aria-label="Main">
        <div style={styles.tabbarInner}>
          <TabButton active={tab === "deals"} taps={tabTaps.deals} onSelect={() => selectTab("deals")} Icon={IconTag} label="Deals" />
          <TabButton active={tab === "nearby"} taps={tabTaps.nearby} onSelect={() => selectTab("nearby")} Icon={IconPin} label="Nearby" />
          <TabButton
            active={tab === "list"} taps={tabTaps.list} onSelect={() => selectTab("list")}
            Icon={IconCart} label="List"
            badge={listMatchCount > 0 ? (<span style={styles.tabBadge} className="tnum">{listMatchCount}</span>) : null}
          />
          <TabButton active={tab === "profile"} taps={tabTaps.profile} onSelect={() => selectTab("profile")} Icon={IconUser} label="Profile" />
        </div>
      </nav>
    </div>
  );
}

const tabBtn = (active) => ({
  flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
  padding: "10px 0 12px", background: "transparent", border: "none", cursor: "pointer",
  color: active ? T.accentDark : T.textMuted,
  fontSize: 11, fontWeight: active ? 700 : 600, fontFamily: "inherit",
});

// v2.1: tab-bar button with springy tap pop + thin-idle/filled-active weight
// change (pageflows-style micro-interaction). `taps` re-keys the icon so the
// pop replays on every tap, even on the already-active tab.
function TabButton({ active, taps, onSelect, Icon, label, badge }) {
  return (
    <button onClick={onSelect} style={tabBtn(active)} aria-current={active}>
      <span
        key={taps}
        style={{
          display: "flex", position: "relative",
          animation: taps > 0 ? "tabPop 0.4s cubic-bezier(0.22,1,0.36,1)" : undefined,
        }}
      >
        <Icon size={22} sw={active ? 2.1 : 1.5} filled={active} />
        {badge}
      </span>
      <span>{label}</span>
    </button>
  );
}

const GLOBAL_CSS = `
  *{box-sizing:border-box;-webkit-tap-highlight-color:transparent;}
  html,body{margin:0;padding:0;overflow-x:hidden;}
  body{background:${T.bg};font-family:${SANS};color:${T.text};}
  input,select,textarea{font-size:16px!important;font-family:${SANS};}
  .tnum{font-variant-numeric:tabular-nums;}
  button{font-family:${SANS};}
`;

const styles = {
  app: { minHeight: "100vh", background: T.bg, color: T.text, fontFamily: SANS },
  header: {
    padding: "26px 0 22px", borderBottom: `1px solid ${T.border}`,
    marginBottom: 20, position: "sticky", top: 0, background: T.bg, zIndex: 10,
  },
  headerInner: { maxWidth: 640, margin: "0 auto", padding: "0 16px" },
  // Premium header: massive display type with tight tracking, a small
  // uppercase micro-label with letter-spacing, crisp 1px hairline,
  // generous whitespace — everything else quiet.
  eyebrow: {
    fontSize: 10.5, fontWeight: 700, textTransform: "uppercase",
    letterSpacing: 2.5, color: T.textSub, marginBottom: 9,
  },
  brand: {
    display: "flex", alignItems: "center", gap: 11,
    fontFamily: SERIF, fontSize: 38, letterSpacing: -1, lineHeight: 1.02,
  },
  logoLeaf: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 34, height: 34, borderRadius: 11, background: T.accentDark, color: "#fff", flexShrink: 0 },
  inner: { maxWidth: 640, margin: "0 auto", padding: "0 14px 170px", width: "100%" },
  demoBanner: { display: "flex", gap: 9, alignItems: "flex-start", fontSize: 12.5, lineHeight: 1.5, color: T.textSub, background: "#FBF3DC", border: "1px solid #EBD9A0", borderRadius: 12, padding: "10px 13px", marginBottom: 14 },
  footer: { fontSize: 12, color: T.textMuted, textAlign: "center", lineHeight: 1.6, marginTop: 22, padding: "0 12px" },
  tabbar: {
    position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 30,
    background: "rgba(255,255,255,0.96)",
    borderTop: `1px solid ${T.border}`,
    paddingBottom: "env(safe-area-inset-bottom)",
  },
  tabbarInner: { maxWidth: 640, margin: "0 auto", display: "flex" },
  tabBadge: {
    position: "absolute", top: -6, right: -10,
    background: T.red, color: "#fff", fontSize: 10, fontWeight: 700,
    minWidth: 17, height: 17, borderRadius: 9,
    display: "flex", alignItems: "center", justifyContent: "center", padding: "0 4px",
  },
};
